import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { CheckCircle, Gift } from 'lucide-react'

const PROVIDER_CONFIG = {
  google: {
    label: 'Googleでログインしました',
    icon: (
      <svg width="16" height="16" viewBox="0 0 48 48" className="shrink-0">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      </svg>
    ),
  },
  rakurip: {
    label: 'ラクリプでログインしました',
    icon: (
      <span className="font-black text-sm leading-none" style={{ color: '#e85d26' }}>R</span>
    ),
  },
}

export function OAuthLoginToast() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // ログイン通知トースト
  const [visible, setVisible] = useState(false)
  const [leaving, setLeaving] = useState(false)

  // ボーナス通知トースト
  const [bonusVisible, setBonusVisible] = useState(false)
  const [bonusLeaving, setBonusLeaving] = useState(false)

  const provider = searchParams.get('oauth') as keyof typeof PROVIDER_CONFIG | null
  const bonus = searchParams.get('bonus')

  // ログイントースト
  useEffect(() => {
    if (!provider || !PROVIDER_CONFIG[provider]) return

    const params = new URLSearchParams(searchParams)
    params.delete('oauth')
    // bonus パラメータは別の useEffect で除去するため残す
    const newSearch = params.toString()
    navigate({ search: newSearch ? `?${newSearch}` : '' }, { replace: true })

    setVisible(true)
    setLeaving(false)
    const leaveTimer = setTimeout(() => setLeaving(true), 3500)
    const hideTimer = setTimeout(() => setVisible(false), 4000)
    return () => {
      clearTimeout(leaveTimer)
      clearTimeout(hideTimer)
    }
  }, [provider])

  // ボーナストースト
  useEffect(() => {
    if (bonus !== 'rakurip') return

    const params = new URLSearchParams(searchParams)
    params.delete('bonus')
    const newSearch = params.toString()
    navigate({ search: newSearch ? `?${newSearch}` : '' }, { replace: true })

    // ログイントーストより少し遅れて表示
    const showTimer = setTimeout(() => {
      setBonusVisible(true)
      setBonusLeaving(false)
    }, 600)
    const leaveTimer = setTimeout(() => setBonusLeaving(true), 5100)
    const hideTimer = setTimeout(() => setBonusVisible(false), 5600)
    return () => {
      clearTimeout(showTimer)
      clearTimeout(leaveTimer)
      clearTimeout(hideTimer)
    }
  }, [bonus])

  return (
    <>
      {/* ログイン通知 */}
      {visible && provider && PROVIDER_CONFIG[provider] && (
        <div
          className={`fixed top-[64px] right-5 z-9999 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border
            bg-white dark:bg-surface
            border-gray-200 dark:border-line
            text-ink dark:text-ink
            transition-all duration-500
            ${leaving ? 'opacity-0 translate-y-[-8px]' : 'opacity-100 translate-y-0'}
          `}
        >
          <CheckCircle className="w-4 h-4 text-ok shrink-0" />
          {PROVIDER_CONFIG[provider].icon}
          <span className="text-sm font-medium">{PROVIDER_CONFIG[provider].label}</span>
        </div>
      )}

      {/* ボーナス通知（ラクリプ連携 500cr 永続ボーナス） */}
      {bonusVisible && (
        <div
          className={`fixed top-[120px] right-5 z-9999 flex items-start gap-3 px-4 py-3 rounded-xl shadow-lg border
            bg-emerald-50 dark:bg-emerald-950/60
            border-emerald-200 dark:border-ok
            transition-all duration-500
            ${bonusLeaving ? 'opacity-0 translate-y-[-8px]' : 'opacity-100 translate-y-0'}
          `}
        >
          <Gift className="w-4 h-4 text-ok shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-emerald-700 dark:text-ok">
              ラクリプ連携ボーナス
            </p>
            <p className="text-xs text-emerald-600 dark:text-ok mt-0.5">
              +500cr を永続クレジットに追加しました
            </p>
          </div>
        </div>
      )}
    </>
  )
}
